import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropdownOptions } from './useDropdownOptions';
import { BrokerOption, FilterOptions } from '../../../Types/filter.types';
import { fetchBrokerOptions, fetchPhysicalCodeOptions, paperTradingCustomerOptions } from './FecthApis';
import { toast } from 'react-toastify';

export const useFilterOptions = () => {
  const {
    clearerOptions,
    clearingAccountOptions,
    bookOptions,
    strategyOptionsList,
    customerCompanyOptions,
    tradeTypeOptions,
    clearerOptionsList,
    commodityOptions,
    tasMmOptions
  } = useDropdownOptions();

  const [brokerOptions, setBrokerOptions] = useState<BrokerOption[]>([]);
  const [isBrokerLoading, setIsBrokerLoading] = useState(false);

  const [physicalCodeOptions, setPhysicalCodeOptions] = useState<any[]>([]);
  const [isPhysicalCodeLoading, setIsPhysicalCodeLoading] = useState(false);

  const buySellOptions = [
    { label: 'Buy', value: 'buy' },
    { label: 'Sell', value: 'sell' },
  ];
  const singleDiff = [
    { label: 'Single', value: 'single' },
    { label: 'Diff', value: 'diff' },
    { label: 'Diff Sub 1', value: 'diff-sub1' },
    { label: 'Diff Sub 2', value: 'diff-sub2' },
  ];
  const traderTypeOptions = [
    { label: 'Cleared', value: 'cleared' },
    { label: 'Bilateral', value: 'bilateral' },
    { label: 'Internal', value: 'internal' },
    
  ];

  const derivativeOptions = [
    { label: 'Swaps', value: 'swaps' },
    { label: 'Futures', value: 'futures' },
  ];

  const tradeStatusOptions = [
    { label: 'Open Trades', value: 'open' },
    { label: 'Expired Trades', value: 'closed' },
  ];

  // Combine loading states
  const loading =
    clearerOptions.isLoading ||
    clearerOptionsList.isLoading ||
    clearingAccountOptions.isLoading ||
    bookOptions.isLoading ||
    
    strategyOptionsList.isLoading ||
    customerCompanyOptions.isLoading ||
    tradeTypeOptions.isLoading ||
    isBrokerLoading ||
    tasMmOptions.isLoading;

  // Combine error states
  const error =
    clearerOptions.error ||
    clearerOptionsList.error ||
    clearingAccountOptions.error ||
    bookOptions.error ||
    strategyOptionsList.error ||
    customerCompanyOptions.error ||
    tradeTypeOptions.error||
    tasMmOptions.error

  const loadBrokerOptions = useCallback(async () => {
    setIsBrokerLoading(true);
    try {
      const { data, error } = await fetchBrokerOptions();

      if (error) {
        toast.error(`Error loading brokers: ${error}`);
        setBrokerOptions([]);
      } else {
        const formattedOptions: BrokerOption[] = Array.isArray(data)
          ? data.map((item: any) => ({
            id: item.id,
            name: item.name,
            label: item.name,
            value: item.id,
          }))
          : [];

        setBrokerOptions(formattedOptions);
      }
    } catch (err) {
      console.error('Error fetching broker options:', err);
      toast.error('Failed to load broker options');
      setBrokerOptions([]);
    } finally {
      setIsBrokerLoading(false);
    }
  }, []);

  const loadPhysicalCodeOptions = useCallback(async () => {
    setIsPhysicalCodeLoading(true);
    try {
      const { data, error } = await fetchPhysicalCodeOptions();

      if (error) {
        toast.error(`Error loading physical codes: ${error}`);
        setPhysicalCodeOptions([]);
      } else {
        const formattedOptions = Array.isArray(data)
          ? data.map((item: any) => ({
            label: item.physical_code,
            value: item.physical_code,
          }))
          : [];

        setPhysicalCodeOptions(formattedOptions);
      }
    } catch (err) {
      console.error('Error fetching physical code options:', err);
      toast.error('Failed to load physical code options');
      setPhysicalCodeOptions([]);
    } finally {
      setIsPhysicalCodeLoading(false);
    }
  }, []);
  useEffect(() => {
    loadBrokerOptions();
    loadPhysicalCodeOptions();
    paperTradingCustomerOptions();
  }, [loadBrokerOptions, loadPhysicalCodeOptions, paperTradingCustomerOptions]);

  const filterOptions: FilterOptions = useMemo(() => ({
    clearer: clearerOptionsList.data?.map((item: any) => ({
      label: item.name,
      value: item.id,
    })) || [],
    clearingAccount: clearingAccountOptions.data?.map((item: any) => ({
      label: item.label,
      value: item.id,
    })) || [],
    book: bookOptions.data?.map((item: any) => ({
      label: item.label,
      value: item.id,
    })) || [],
    customers: customerCompanyOptions.data?.map((item: any) => ({
      label: item.customer_company_name,
      value: item.customer_company_name,
    })) || [],
    strategy: strategyOptionsList.data?.map((item: any) => ({
      label: item.label,
      value: item.id,
    })) || [],
    contract_type_name: tradeTypeOptions.data?.map((item: any) => ({
      label: item.label,
      value: item.id,
    })) || [],
    trader_type: traderTypeOptions,
    buySell: buySellOptions,
    physicalCode: physicalCodeOptions,
    singleDiff: singleDiff,
    derivative: derivativeOptions,
    trade_status: tradeStatusOptions,
    commodity: commodityOptions.data?.map((item: any) => ({
      label: item.contract_name,
      value: item.id,
    })) || [],
    tas_mm: tasMmOptions.data?.map((item: any) => ({
      label: item.label,
      value: item.value,
    })) || [],
    broker: brokerOptions.map(item => ({
      label: item.name,
      value: item.id,
    })),
  }), [
    clearerOptions.data,
    clearingAccountOptions.data,
    bookOptions.data,
    customerCompanyOptions.data,
    strategyOptionsList.data,
    tradeTypeOptions.data,
    commodityOptions,
    brokerOptions,
    physicalCodeOptions,
    tasMmOptions.data
  ]);

  return {
    filterOptions,
    loading,
    error: error ? 'Failed to load filter options' : null,
    loadBrokerOptions,
    isBrokerLoading,
    loadPhysicalCodeOptions,
    isPhysicalCodeLoading
  };
};
